/**
 * ============================================================
 * UPSELL INLINE COMPONENT — upsell-inline.js
 * Plaatsing: assets/upsell-inline.js
 *
 * Werking:
 *  - Luistert op klikken op .upsell-btn knoppen
 *  - Voegt variant toe via Shopify AJAX Cart API (/cart/add.js)
 *  - Toont inline feedback (success / error) per kaart
 *  - Dispatcht 'cart:updated' event zodat cart-iconen bijwerken
 *
 * Vereisten:
 *  - Shopify thema met standaard AJAX cart ondersteuning
 *  - Geen externe dependencies
 * ============================================================
 */

(function () {
  'use strict';

  /* ── Configuratie ──────────────────────────────────────── */

  const CONFIG = {
    /* Teksten — pas aan voor jouw taal / branding */
    messages: {
      success:        'Toegevoegd aan je winkelwagen!',
      error_generic:  'Er ging iets mis. Probeer het opnieuw.',
      error_sold_out: 'Dit product is helaas uitverkocht.',
      error_network:  'Geen verbinding. Controleer je internet en probeer opnieuw.',
      btn_success:    'Toegevoegd ✓',
    },

    /* Timing (milliseconden) */
    feedbackDuration:   4000,   /* Hoe lang feedback zichtbaar blijft */
    successBtnDuration: 2500,   /* Hoe lang knop in success-state blijft */

    /* Shopify cart API endpoint */
    cartAddUrl: '/cart/add.js',
    cartGetUrl: '/cart.js',
  };

  /* ── Selectors ─────────────────────────────────────────── */

  const SELECTORS = {
    section:  '[data-upsell-section]',
    card:     '[data-upsell-card]',
    btn:      '[data-upsell-btn]',
    feedback: '[data-upsell-feedback]',
  };

  /* ── Initialisatie ─────────────────────────────────────── */

  function init() {
    const sections = document.querySelectorAll(SELECTORS.section);

    if (!sections.length) return;

    sections.forEach(attachSectionListeners);
  }

  /**
   * Voeg event listeners toe aan één upsell sectie.
   * Gebruikt event delegation voor efficiency.
   */
  function attachSectionListeners(section) {
    section.addEventListener('click', handleClick);
  }

  /* ── Click handler ─────────────────────────────────────── */

  async function handleClick(event) {
    const btn = event.target.closest(SELECTORS.btn);

    if (!btn) return;
    if (btn.disabled || btn.classList.contains('upsell-btn--loading')) return;

    const variantId = parseInt(btn.dataset.variantId, 10);

    if (!variantId || isNaN(variantId)) {
      console.error('[Upsell] Geen geldig variant ID gevonden op knop:', btn);
      return;
    }

    const card = btn.closest(SELECTORS.card);

    await addToCart(btn, card, variantId);
  }

  /* ── Cart API call ─────────────────────────────────────── */

  /**
   * Voegt een variant toe aan de Shopify cart via AJAX API.
   *
   * @param {HTMLElement} btn       - De knop die geklikt werd
   * @param {HTMLElement} card      - De upsell kaart container
   * @param {number}      variantId - Shopify variant ID
   */
  async function addToCart(btn, card, variantId) {
    setLoadingState(btn, true);
    clearFeedback(card);

    const payload = {
      id: variantId,
      quantity: 1,
    };

    try {
      const response = await fetch(CONFIG.cartAddUrl, {
        method:  'POST',
        headers: {
          'Content-Type':     'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept':           'application/json',
        },
        body: JSON.stringify(payload),
      });

      /* ── Response verwerking ── */

      if (response.ok) {
        const data = await response.json();
        handleSuccess(btn, card, data);

      } else {
        /* Probeer Shopify's eigen foutmelding te lezen */
        let errorMessage = CONFIG.messages.error_generic;

        try {
          const errorData = await response.json();

          if (response.status === 422) {
            /* 422 = variant niet beschikbaar of uitverkocht */
            errorMessage = errorData.description || CONFIG.messages.error_sold_out;
          } else if (errorData.description) {
            errorMessage = errorData.description;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (_) {
          /* JSON parse mislukt — gebruik generieke melding */
        }

        handleError(btn, card, errorMessage, response.status);
      }

    } catch (networkError) {
      /* Geen verbinding of request geblokkeerd */
      console.error('[Upsell] Network error:', networkError);
      handleError(btn, card, CONFIG.messages.error_network, 0);
    }
  }

  /* ── Success ───────────────────────────────────────────── */

  function handleSuccess(btn, card, cartData) {
    setLoadingState(btn, false);
    setSuccessState(btn);
    showFeedback(card, CONFIG.messages.success, 'success');
    markCardAdded(card);

    /* Stuur event naar thema — bijwerken cart count / drawer */
    dispatchCartUpdate(cartData);

    /* Reset knop na succesinterval */
    setTimeout(() => {
      resetBtnText(btn);
    }, CONFIG.successBtnDuration);
  }

  /* ── Error ─────────────────────────────────────────────── */

  function handleError(btn, card, message, statusCode) {
    setLoadingState(btn, false);

    /* Log voor debugging */
    console.warn(`[Upsell] Cart error (${statusCode}):`, message);

    showFeedback(card, message, 'error');

    /* Schud de knop subtiel om fout te signaleren */
    shakeBtn(btn);
  }

  /* ── UI helpers ────────────────────────────────────────── */

  /** Zet knop in loading state */
  function setLoadingState(btn, isLoading) {
    btn.classList.toggle('upsell-btn--loading', isLoading);
    btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  /** Zet knop in success state */
  function setSuccessState(btn) {
    btn.classList.add('upsell-btn--success');

    const textEl = btn.querySelector('.upsell-btn-text');
    const iconEl = btn.querySelector('.upsell-btn-icon');

    if (textEl) {
      textEl.textContent = CONFIG.messages.btn_success;
      textEl.style.opacity = '1';
    }
    if (iconEl) iconEl.style.opacity = '0';
  }

  /** Reset knoptekst naar oorspronkelijke tekst */
  function resetBtnText(btn) {
    /* Verwijder succes-classes maar laat kaart gemarkeerd */
    btn.classList.remove('upsell-btn--success');

    const textEl = btn.querySelector('.upsell-btn-text');
    const iconEl = btn.querySelector('.upsell-btn-icon');

    /* Haal originele tekst op uit data-attribute als dat gezet is,
       anders valt terug op standaard tekst */
    if (textEl) {
      textEl.textContent = btn.dataset.originalText || 'Toevoegen';
    }
    if (iconEl) iconEl.style.opacity = '1';
  }

  /** Voeg visuele "toegevoegd" overlay toe aan kaart */
  function markCardAdded(card) {
    card.classList.add('upsell-card--added');
  }

  /** Toon feedback bericht in kaart */
  function showFeedback(card, message, type) {
    const feedbackEl = card.querySelector(SELECTORS.feedback);
    if (!feedbackEl) return;

    /* Reset vorige state */
    feedbackEl.className = 'upsell-feedback';
    feedbackEl.textContent = '';

    /* Forceer reflow zodat animatie herstart */
    void feedbackEl.offsetHeight;

    feedbackEl.textContent = message;
    feedbackEl.classList.add(`upsell-feedback--${type}`, 'is-visible');

    /* Auto-verbergen */
    if (type !== 'error') {
      setTimeout(() => hideFeedback(feedbackEl), CONFIG.feedbackDuration);
    }
  }

  /** Verberg feedback element */
  function hideFeedback(feedbackEl) {
    feedbackEl.classList.remove('is-visible');
  }

  /** Wis eventuele eerdere feedback */
  function clearFeedback(card) {
    const feedbackEl = card.querySelector(SELECTORS.feedback);
    if (!feedbackEl) return;

    feedbackEl.classList.remove('is-visible', 'upsell-feedback--success', 'upsell-feedback--error');
    feedbackEl.textContent = '';
  }

  /** Schud animatie op knop bij fout */
  function shakeBtn(btn) {
    btn.style.animation = 'none';
    void btn.offsetHeight;
    btn.style.animation = 'upsellShake 0.4s ease';

    btn.addEventListener('animationend', () => {
      btn.style.animation = '';
    }, { once: true });
  }

  /* ── Cart update events ────────────────────────────────── */

  /**
   * Dispatcht twee events die door de meeste Shopify thema's worden opgevangen:
   *  1. Standaard CustomEvent 'cart:refresh' (Dawn, Impulse, e.d.)
   *  2. 'cart:updated' met cart data als detail (minder standaard)
   *
   * Als jouw thema een ander event verwacht, voeg dat hier toe.
   */
  function dispatchCartUpdate(cartData) {
    /* Dawn / standaard thema's */
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    /* Extra event met cart data */
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartData },
      })
    );

    /* Refresht cart count badge in Dawn thema */
    updateCartCountUI(cartData);
  }

  /**
   * Probeert de cart count badge in de navigatie bij te werken.
   * Ondersteunt Dawn-thema's bubble count selector.
   * Voeg extra selectors toe als jouw thema andere class-namen gebruikt.
   */
  function updateCartCountUI(cartData) {
    /* ── AANPASSEN: selectors voor jouw thema's cart bubble ── */
    const cartCountSelectors = [
      '.cart-count-bubble',     /* Dawn thema */
      '[data-cart-count]',
      '.cart-item-count',
      '#cart-icon-bubble',
    ];

    const itemCount = cartData?.item_count ?? null;
    if (itemCount === null) return;

    cartCountSelectors.forEach(selector => {
      const els = document.querySelectorAll(selector);
      els.forEach(el => {
        /* Probeer eerste tekstknoop bij te werken */
        const textNode = el.querySelector('span[aria-hidden="true"]') || el;
        if (textNode) textNode.textContent = itemCount;

        /* Zorg dat element zichtbaar is als count > 0 */
        el.style.display = itemCount > 0 ? '' : 'none';
      });
    });
  }

  /* ── CSS animatie injecteren ───────────────────────────── */

  /**
   * Injecteert de shake-keyframe animatie dynamisch.
   * Hierdoor is de JS volledig standalone zonder extra CSS vereist voor deze animatie.
   */
  function injectShakeAnimation() {
    if (document.getElementById('upsell-shake-style')) return;

    const style = document.createElement('style');
    style.id = 'upsell-shake-style';
    style.textContent = `
      @keyframes upsellShake {
        0%  { transform: translateX(0); }
        20% { transform: translateX(-4px); }
        40% { transform: translateX(4px); }
        60% { transform: translateX(-3px); }
        80% { transform: translateX(3px); }
        100%{ transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
  }

  /* ── Cache originele knopteksten ───────────────────────── */

  /**
   * Sla per knop de originele tekst op als data-attribute,
   * zodat resetBtnText() altijd de correcte tekst kan herstellen.
   */
  function cacheOriginalBtnTexts() {
    document.querySelectorAll(SELECTORS.btn).forEach(btn => {
      const textEl = btn.querySelector('.upsell-btn-text');
      if (textEl && !btn.dataset.originalText) {
        btn.dataset.originalText = textEl.textContent.trim();
      }
    });
  }

  /* ── Opstarten ─────────────────────────────────────────── */

  function bootstrap() {
    injectShakeAnimation();
    cacheOriginalBtnTexts();
    init();
  }

  /* DOMContentLoaded of direct als DOM al klaar is */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

})();
